'use client';

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useReveal } from '@/hooks/use-reveal';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import type { GalleryCategory, GalleryImage } from '@/lib/gallery';

interface GalleryClientProps {
  categories: GalleryCategory[];
  totalImages: number;
}

interface VisibleImage extends GalleryImage {
  catName: string;
  catSlug: string;
}

export default function GalleryClient({ categories, totalImages }: GalleryClientProps) {
  const { ref, visible } = useReveal();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [lightbox, setLightbox] = useState<{ catSlug: string; imageIndex: number } | null>(null);

  // All images flattened for "all" view
  const allImages = useMemo<VisibleImage[]>(() => {
    return categories.flatMap(c => c.images.map(img => ({ ...img, catName: c.name, catSlug: c.slug })));
  }, [categories]);

  const visibleImages = useMemo<VisibleImage[]>(() => {
    if (activeCategory === 'all') return allImages;
    const cat = categories.find(c => c.slug === activeCategory);
    if (!cat) return [];
    return cat.images.map(img => ({ ...img, catName: cat.name, catSlug: cat.slug }));
  }, [activeCategory, allImages, categories]);

  const navigateLightbox = useCallback((dir: number) => {
    setLightbox((current) => {
      if (!current) return null;
      const currentList = activeCategory === 'all' ? allImages : (categories.find(c => c.slug === current.catSlug)?.images || []);
      const currentIdx = currentList.findIndex(img => img.index === current.imageIndex && img.category === current.catSlug);
      if (currentIdx === -1) return current;
      const nextIdx = (currentIdx + dir + currentList.length) % currentList.length;
      const next = currentList[nextIdx];
      return { catSlug: next.category, imageIndex: next.index };
    });
  }, [activeCategory, allImages, categories]);

  // Lightbox keyboard navigation
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
      else if (e.key === 'ArrowRight') navigateLightbox(1);
      else if (e.key === 'ArrowLeft') navigateLightbox(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox, navigateLightbox]);

  const currentLightboxImage = lightbox
    ? visibleImages.find(img => img.category === lightbox.catSlug && img.index === lightbox.imageIndex)
    : null;

  return (
    <section id="galerie" ref={ref} className="relative py-28 md:py-40 bg-[#F8F1E4] overflow-hidden">
      <div className="container mx-auto px-6 relative">
        {/* Heading */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className={`reveal ${visible ? 'visible' : ''} mb-6`}>
            <div className="flex items-center justify-center gap-4">
              <div className="w-10 h-px bg-[#8B4513]" />
              <span className="text-[#8B4513] font-serif-display text-sm tracking-[0.4em] uppercase">
                La Galerie
              </span>
              <div className="w-10 h-px bg-[#8B4513]" />
            </div>
          </div>

          <h2 className={`reveal ${visible ? 'visible' : ''} font-display text-[#2B1810] text-4xl md:text-6xl lg:text-7xl font-medium leading-[1.05] mb-6`}>
            Notre Collection
            <br />
            <span className="italic text-luxe-gold">Complète</span>
          </h2>

          <p className={`reveal ${visible ? 'visible' : ''} text-[#5C2E03]/80 text-lg md:text-xl font-serif-display font-light leading-relaxed mb-4`}>
            Explorez l'intégralité de nos créations, classées par catégorie. Chaque image révèle
            une nuance, un détail de couture, une patine unique.
          </p>
          <p className={`reveal ${visible ? 'visible' : ''} text-[#8B4513] text-xs tracking-[0.3em] uppercase font-serif-display`}>
            {totalImages} pièces · {categories.length} catégories
          </p>
        </div>

        {/* Filter tabs */}
        <div className={`reveal ${visible ? 'visible' : ''} flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-12`}>
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-serif-display transition-all duration-300 border ${
              activeCategory === 'all'
                ? 'bg-[#2B1810] text-[#F8F1E4] border-[#2B1810]'
                : 'bg-transparent text-[#2B1810] border-[#8B4513]/30 hover:border-[#8B4513]'
            }`}
          >
            Tout voir
          </button>
          {categories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => setActiveCategory(cat.slug)}
              className={`px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-serif-display transition-all duration-300 border flex items-center gap-2 ${
                activeCategory === cat.slug
                  ? 'bg-[#2B1810] text-[#F8F1E4] border-[#2B1810]'
                  : 'bg-transparent text-[#2B1810] border-[#8B4513]/30 hover:border-[#8B4513]'
              }`}
            >
              {cat.name}
              {cat.comingSoon ? (
                <span className="text-[9px] tracking-[0.15em] uppercase opacity-70 italic">
                  Bientôt
                </span>
              ) : (
                <span className="opacity-60">· {cat.images.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Category description */}
        {activeCategory !== 'all' && (
          <div className="max-w-2xl mx-auto text-center mb-12">
            <p className="text-[#5C2E03]/75 text-base font-serif-display italic leading-relaxed">
              {categories.find(c => c.slug === activeCategory)?.description}
            </p>
            {categories.find(c => c.slug === activeCategory)?.comingSoon && (
              <div className="mt-6 inline-flex items-center gap-3 px-5 py-2.5 bg-[#C9A961]/10 border border-[#C9A961]/40">
                <span className="w-2 h-2 rounded-full bg-[#C9A961] animate-pulse" />
                <span className="text-[#8B4513] text-[10px] tracking-[0.3em] uppercase font-serif-display">
                  Collection en préparation — Bientôt disponible
                </span>
              </div>
            )}
          </div>
        )}

        {/* Masonry-like grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {visibleImages.map((img, idx) => {
            const isPlaceholder = img.index === 0 && (activeCategory === 'all' ? true : categories.find(c => c.slug === img.catSlug)?.comingSoon);
            const parentCat = categories.find(c => c.slug === img.catSlug);
            return (
              <button
                key={`${img.catSlug}-${img.index}`}
                onClick={() => !isPlaceholder && setLightbox({ catSlug: img.catSlug, imageIndex: img.index })}
                className={`reveal ${visible ? 'visible' : ''} group relative aspect-square overflow-hidden bg-[#2B1810] ${isPlaceholder ? 'cursor-default' : 'cursor-pointer'}`}
                style={{ transitionDelay: `${Math.min(idx * 30, 600)}ms` }}
              >
                <img
                  src={img.src}
                  alt={`${img.catName} ASMAO ${img.index}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
                />
                {/* For real images: dark gradient overlay on hover */}
                {!isPlaceholder && (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2B1810]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute bottom-3 left-3 right-3 text-left opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-3 group-hover:translate-y-0">
                      <div className="text-[#C9A961] text-[9px] tracking-[0.3em] uppercase font-serif-display">
                        {img.catName}
                      </div>
                      <div className="text-[#F8F1E4] text-xs font-serif-display">
                        N°{String(img.index).padStart(2, '0')}
                      </div>
                    </div>
                  </>
                )}
                {/* For placeholder images: permanent "Bientôt disponible" overlay */}
                {isPlaceholder && parentCat?.comingSoon && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[#2B1810]/30">
                    <div className="absolute top-3 right-3 px-2.5 py-1 bg-[#C9A961] text-[#1A0E08] text-[9px] tracking-[0.2em] uppercase font-serif-display font-bold">
                      Bientôt
                    </div>
                  </div>
                )}
                {/* Corner ornaments */}
                <div className="absolute top-2 left-2 w-4 h-4 border-l border-t border-[#C9A961]/0 group-hover:border-[#C9A961]/80 transition-all duration-500" />
                <div className="absolute top-2 right-2 w-4 h-4 border-r border-t border-[#C9A961]/0 group-hover:border-[#C9A961]/80 transition-all duration-500" />
              </button>
            );
          })}
        </div>

        {/* Empty state — either "Bientôt disponible" (coming soon) or "loading" */}
        {visibleImages.length === 0 && (
          <div className="text-center py-20">
            {activeCategory !== 'all' && categories.find(c => c.slug === activeCategory)?.comingSoon ? (
              // Coming soon — elegant placeholder
              <div className="max-w-md mx-auto">
                <div className="relative w-32 h-32 mx-auto mb-8">
                  <div className="absolute inset-0 rounded-full border border-[#C9A961]/30" />
                  <div className="absolute inset-2 rounded-full border border-[#C9A961]/40" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-display text-[#C9A961] text-4xl tracking-wider">
                      {activeCategory.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
                <h3 className="font-display text-[#2B1810] text-2xl mb-3">
                  {categories.find(c => c.slug === activeCategory)?.name}
                </h3>
                <p className="text-[#5C2E03]/70 font-serif-display italic leading-relaxed mb-6">
                  {categories.find(c => c.slug === activeCategory)?.description}
                </p>
                <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#2B1810] text-[#F8F1E4]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A961] animate-pulse" />
                  <span className="text-[10px] tracking-[0.3em] uppercase font-serif-display">
                    Collection en préparation
                  </span>
                </div>
                <p className="text-[#8B4513]/60 text-xs font-serif-display italic mt-6">
                  Nos artisans façonnent actuellement cette collection. Revenez bientôt pour
                  découvrir nos créations, ou contactez-nous pour une commande sur mesure.
                </p>
              </div>
            ) : (
              // Default loading state
              <>
                <div className="inline-block w-12 h-12 border-2 border-[#C9A961] border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-[#5C2E03]/70 font-serif-display italic">
                  Génération des images en cours. Revenez dans quelques instants…
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && currentLightboxImage && (
        <div
          className="fixed inset-0 z-[100] bg-[#0a0604]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
          onClick={() => setLightbox(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 w-12 h-12 rounded-full border border-[#C9A961]/50 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#2B1810] transition-all duration-300 flex items-center justify-center z-10"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>

          {/* Prev */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateLightbox(-1);
            }}
            className="absolute left-4 md:left-8 w-12 h-12 rounded-full border border-[#C9A961]/50 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#2B1810] transition-all duration-300 flex items-center justify-center"
            aria-label="Précédent"
          >
            <ChevronLeft size={24} />
          </button>

          {/* Image */}
          <div
            className="relative max-w-3xl max-h-[85vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentLightboxImage.src}
              alt={`${currentLightboxImage.catName} ASMAO STYLE CUIR ${currentLightboxImage.index}`}
              className="w-full h-full object-contain max-h-[80vh]"
            />
            <div className="mt-4 text-center">
              <div className="text-[#C9A961] text-[10px] tracking-[0.4em] uppercase font-serif-display mb-1">
                {currentLightboxImage.catName}
              </div>
              <div className="text-[#F8F1E4]/80 text-sm font-serif-display italic">
                Pièce N°{String(currentLightboxImage.index).padStart(2, '0')} · ASMAO STYLE CUIR
              </div>
            </div>
          </div>

          {/* Next */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateLightbox(1);
            }}
            className="absolute right-4 md:right-8 w-12 h-12 rounded-full border border-[#C9A961]/50 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#2B1810] transition-all duration-300 flex items-center justify-center"
            aria-label="Suivant"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      )}
    </section>
  );
}
